/**
 * Frozen attack arrow for the time-machine / snapshot view.
 *
 * A log row for an attack already highlights the two territories and freezes
 * the "−N, −M" badges, but on a busy board that pair does not say which way
 * the attack went. This draws a red from→to arrow between them, the same
 * shape as the fortify snapshot's teal arrow, so direction reads at a glance.
 * Live attacks are not affected: the layer only renders for `staticEntry`.
 */
import React from 'react'
import { Platform } from 'react-native'
import { G } from 'react-native-svg'
import { DirectionArrow } from './DirectionArrow'
import type { AttackAnimationEntry } from '../../hooks/useAttackEvents'

/** Blood red, with the white halo DirectionArrow draws under every shaft. */
export const ATTACK_ARROW_COLOR = '#DC2626'

type Point = { x: number; y: number }
type PositionLookup = Record<string, Point | undefined>

/**
 * Endpoints for the frozen attack arrow: both ends prefer the PoI-corrected
 * badge position (a badgeAnchor override moves the troop number, and the
 * arrow must follow it), falling back to the blueprint centroid.
 */
export function attackArrowEndpoints(
  entry: Pick<AttackAnimationEntry, 'attackerTerritoryId' | 'defenderTerritoryId'>,
  positionById: PositionLookup,
  badgePositionById: PositionLookup,
): { from: Point; to: Point } | null {
  const from = badgePositionById[entry.attackerTerritoryId] ?? positionById[entry.attackerTerritoryId]
  const to = badgePositionById[entry.defenderTerritoryId] ?? positionById[entry.defenderTerritoryId]
  return from && to ? { from, to } : null
}

interface AttackArrowLayerProps {
  staticEntry?: AttackAnimationEntry
  positionById: PositionLookup
  badgePositionById: PositionLookup
  /** Badge circle radius in viewBox units (MapView's badgeRadius). */
  badgeRadius: number
  /** Trail stroke width in viewBox units (MapView's fortify trail width). */
  trailWidth: number
}

export function AttackArrowLayer({ staticEntry, positionById, badgePositionById, badgeRadius, trailWidth }: AttackArrowLayerProps) {
  if (!staticEntry) return null
  const ends = attackArrowEndpoints(staticEntry, positionById, badgePositionById)
  if (!ends) return null
  return (
    <G
      testID="attack-result-arrow"
      pointerEvents="none"
      {...(Platform.OS === 'ios' && { accessibilityElementsHidden: true })}
      {...(Platform.OS === 'android' && { importantForAccessibility: 'no-hide-descendants' as const })}
      {...(Platform.OS === 'web' && ({ 'aria-hidden': true } as { 'aria-hidden': true }))}
    >
      <DirectionArrow from={ends.from} to={ends.to} badgeRadius={badgeRadius} trailWidth={trailWidth} color={ATTACK_ARROW_COLOR} />
    </G>
  )
}
